import React, {useCallback, useMemo, useState, useEffect} from 'react'
import styled from 'styled-components'
import {useForm} from 'react-hook-form'
import {
  AUTH_LOGIN_MAX_ATTEMPT,
  OTP_COOLDOWN,
  WINDOW_MODE_MOBILE_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {
  EmailForm,
  GiftshopProfileVerifyPassword,
  ObjectState,
  ProfileEditEmailFieldType,
  ProfileEditScreenType,
  ProfileEditSelfiePage,
  ProfileEditVerifMethod,
  ProfileEditVerifStatus,
  ProfileVerifyEmailErrorMessage,
} from 'types'
import {requestData} from 'services'
import {
  formatCooldownTime,
  formatPhoneNumber,
  showSnackbar,
  useDidMount,
  useHistory,
} from 'utils'
import {Button, Input, Paragraph} from 'common/components'
import {useDispatch, useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {
  VALIDATION_AUTH_LOGIN_PASSWORD,
  VALIDATION_GIFTSHOP_EDITPROFILE_EMAIL,
} from 'common/validations'
import {
  GiftShopTemplateModalMFA,
  GiftShopTemplateOTPModal,
} from '../../../template'
import {
  GiftShopEditProfileLimitPasswordModal,
  GiftShopEditProfileModalVerifMethod,
  GiftShopEditProfileModalVerifStatus,
  GiftShopEditProfilePasswordVerifyModal,
} from '../Modal'
import {GiftShopEditProfileSelfie} from '../Selfie'

interface ScreenProps {
  isKyc: boolean
}

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: ${convertUnit(25)};
  border-radius: ${convertUnit(30)};
`

const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const StyledScreenContainer = styled.div<ScreenProps>`
  display: flex;
  flex: 1;
  margin: ${({isKyc}) => (isKyc ? 0 : convertUnit(25))};
  padding: ${({isKyc}) => (isKyc ? 0 : convertUnit(50))};
  border-radius: ${convertUnit(8)};
  background-color: ${({theme}) => theme.white_1};
  flex-direction: column;
  max-width: ${({isKyc}) => (isKyc ? `100%` : convertUnit(770))};
  height: ${({isKyc}) => (isKyc ? `100%` : `max-content`)};

  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    padding: 0;
  }
`

const StyledError = styled(Paragraph)`
  margin-top: ${convertUnit(4)};
`

interface GiftshopEmailFormProps {
  stateActiveContent: ObjectState<string>
  stateModalOtp: ObjectState<boolean>
  stateIsFilled: ObjectState<boolean>
  stateEditProfileScreen?: ObjectState<ProfileEditScreenType>
  stateKycScreen?: ObjectState<ProfileEditSelfiePage>
}

export default function GiftshopEmailForm({
  stateEditProfileScreen,
  stateKycScreen,
  stateActiveContent,
  stateModalOtp,
  stateIsFilled,
}: GiftshopEmailFormProps) {
  const setActiveContent = stateActiveContent[1]
  const {translate} = useTranslation()
  const {update} = useDispatch()
  const history = useHistory()
  const {has_password, email: oldEmail, phone_code, phone_number} =
    useSelector('user') || {}
  const {emailTime} = useSelector('lastUserState')
  const {email: newEmail} = useSelector('userNewEmail') || {}
  const {
    isOldEmailConfirmed,
    verifMethodEmail,
    showModalEmailOtp,
    showNewEmail,
  } = useSelector('editProfileState') || {}
  const [modalVerifMethod, setModalVerifMethod] = useState(false)
  const [modalOtp, setModalOtp] = stateModalOtp
  const stateOtpError = useState(false)
  const setOtpError = stateOtpError[1]
  const [otpErrorCode, setOtpErrorCode] = useState(200)
  const [resetWhenPress, setResetWhenPress] = useState(true)
  const [modalVerifStatus, setModalVerifStatus] = useState(false)
  const [verifStatus, setVerifStatus] = useState<
    ProfileEditVerifStatus | undefined
  >()
  const [shouldResendOtp, setShouldResendOtp] = useState(modalOtp)
  const [selectedMethod, setSelectedMethod] = useState<
    ProfileEditVerifMethod | undefined
  >()

  const form = useForm<EmailForm>({
    defaultValues: {
      email: showNewEmail ? newEmail : '',
      password: '',
    },
    reValidateMode: 'onChange',
  })
  const {handleSubmit, watch, errors, setValue} = form
  const {email, password} = watch()

  const [publishedState, setPublishedState] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingInModal, setLoadingInModal] = useState(false)
  const [passwordLimitModal, setPasswordLimitModal] = useState(false)
  const [cooldownTimer, setCooldownTimer] = useState('0')
  const [
    errorMessage,
    setErrorMessage,
  ] = useState<ProfileVerifyEmailErrorMessage>({
    type: '' as ProfileEditEmailFieldType,
    message: '',
  })
  const [passwordVerifyModal, setPasswordVerifyModal] = useState<{
    visible: boolean
    item: GiftshopProfileVerifyPassword
  }>({
    visible: false,
    item: 'none',
  })
  const prompt = useMemo(
    () => (email !== '' || password !== '') && !publishedState,
    [email, password, publishedState],
  )
  const setisFilled = stateIsFilled[1]
  const screen = stateEditProfileScreen && stateEditProfileScreen[0]
  const setScreen = stateEditProfileScreen && stateEditProfileScreen[1]
  const stateKycActiveScreen = stateKycScreen
  const statePassword = useState('')
  const isEditEmail = useMemo(() => !!oldEmail, [oldEmail])
  const stateModalMFA = useState(false)
  const setModalMFA = stateModalMFA[1]
  const stateMFALoading = useState(false)
  const setMFALoading = stateMFALoading[1]
  const stateMFAError = useState('')
  const setMFAError = stateMFAError[1]
  const [suspendTitle, setSuspendTitle] = useState<string | undefined>()

  const handlePasswordVerifyModal = useCallback(
    (item: GiftshopProfileVerifyPassword) =>
      setPasswordVerifyModal((prev) => ({visible: !prev.visible, item})),
    [],
  )

  const handleSuccessSendEmail = useCallback(() => {
    update('lastUserState', {emailTime: new Date().getTime()})
    history.push('auth_verification_email', {
      email,
      disableCensor: true,
    })
  }, [email, history, update])

  const handleResetEditEmailStatus = useCallback(() => {
    update('editProfileState', {
      isOldEmailConfirmed: false,
    })
  }, [update])

  const handleResendEmail = useCallback(async () => {
    setLoadingInModal(true)
    setLoading(true)
    const response = await requestData('member_2fa_edit_email_resend_new')
    if (typeof response !== 'string') {
      if (response.status === 200) {
        handleSuccessSendEmail()
      } else if (
        response.status === 422 &&
        response.data.message === 'no prior request'
      ) {
        handleResetEditEmailStatus()
        setModalVerifMethod(true)
      } else if (response.status === 429) {
        const {resend_in} = response.data.detail || {}
        const {hour, minute, second} = formatCooldownTime(
          resend_in,
          false,
          true,
        )
        showSnackbar(
          translate('auth:resendLimitReached', {
            duration: hour + minute + second,
          }),
        )
      } else {
        setVerifStatus('failed')
        setModalVerifStatus(true)
      }
    }
    setLoadingInModal(false)
    setLoading(false)
  }, [handleResetEditEmailStatus, handleSuccessSendEmail, translate])

  const handle2FAEditEmailSendNew = useCallback(() => {
    setLoadingInModal(true)
    requestData('member_2fa_edit_email_send_new', {
      onRequestReceived: () => {
        setLoadingInModal(false)
      },
      onRequestSuccess: ({status}) => {
        setModalOtp(false)
        if (status === 200) {
          update('lastUserState', {
            emailTime: new Date().getTime(),
          })
          setVerifStatus('success')
          setModalVerifStatus(true)
        } else {
          showSnackbar(
            translate('global:messageError', {context: `code-${status}`}),
          )
        }
      },
    })
  }, [setModalOtp, translate, update])

  const handleVerifyOtp = useCallback(
    (code: string) => {
      requestData('member_2fa_edit_email_verify_current', {
        data: {code},
        onRequestSuccess: ({status, data: {detail}}) => {
          if (status === 200) {
            setPublishedState(true)
            if (!isEditEmail) {
              setModalOtp(false)
            }
            update('editProfileState', {
              isOldEmailConfirmed: true,
              verifMethodEmail: undefined,
            })
            handle2FAEditEmailSendNew()
          } else if (status === 400 && detail) {
            if (detail.code === 'code does not match') {
              setOtpErrorCode(404)
              setOtpError(true)
            }
          } else if (status === 410) {
            setOtpErrorCode(410)
            setOtpError(false)
          } else {
            setModalOtp(false)
            setVerifStatus('failed')
            setModalVerifStatus(true)
          }
        },
      })
    },
    [handle2FAEditEmailSendNew, isEditEmail, setModalOtp, setOtpError, update],
  )

  const handleNavigateToKyc = useCallback(() => {
    setScreen && setScreen('kyc')
  }, [setScreen])

  const handleUpdateOtpTimeEmail = useCallback(() => {
    update('lastUserState', {emailTime: new Date().getTime()})
    setResetWhenPress(true)
    setShouldResendOtp(true)
    setModalOtp(true)
  }, [setModalOtp, update])

  const handleOnSendFirstOtpSuccess = useCallback(
    (method: ProfileEditVerifMethod) => {
      email && update('userNewEmail', {email})
      update('editProfileState', {
        isOldEmailConfirmed: false,
        verifMethodEmail: method,
        showNewEmail: true,
      })
      update('verificationStatus', {
        isEmailVerified: false,
      })
      setModalVerifMethod(false)
      if (method === 'kyc') {
        statePassword[1](password)
        handleNavigateToKyc()
      } else {
        handleUpdateOtpTimeEmail()
      }
    },
    [
      email,
      handleNavigateToKyc,
      handleUpdateOtpTimeEmail,
      password,
      statePassword,
      update,
    ],
  )

  const handle2FAEditEmail = useCallback(
    (method?: ProfileEditVerifMethod, totp?: string) => {
      if (method) {
        setLoadingInModal(true)
      } else {
        setLoading(true)
        setMFALoading(true)
      }
      requestData('member_2fa_edit_email_send_current', {
        data: {email, password, verification_method: method, mfa_code: totp},
        onRequestReceived: () => {
          setLoadingInModal(false)
          setLoading(false)
          setModalVerifMethod(false)
          setMFALoading(false)
        },
        onRequestSuccess: ({status, data: {message, detail, result}}) => {
          setPublishedState(true)
          if (status === 200) {
            setModalMFA(false)
            setMFAError('')
            if (method) {
              handleOnSendFirstOtpSuccess(method)
            } else {
              setModalVerifMethod(true)
            }
          } else if (status === 202) {
            if (result.has_mfa && method) {
              setModalMFA(true)
            } else {
              setModalVerifMethod(true)
            }
          } else if (status === 400 && detail) {
            if (detail.attempt) {
              // wrong password
              setErrorMessage({
                type: 'password',
                message: translate('auth:validationLoginAttempt', {
                  attempt:
                    AUTH_LOGIN_MAX_ATTEMPT - parseInt(detail.attempt, 10),
                }),
              })
            } else if (detail.mfa_attempt) {
              // wrong totp
              setMFAError(
                translate('giftShop:settingAuthAppInvalidError', {
                  count:
                    AUTH_LOGIN_MAX_ATTEMPT - parseInt(detail.mfa_attempt, 10),
                }),
              )
            }
          } else if (status === 409) {
            setErrorMessage({
              type: 'email',
              message: translate('giftShop:addEmailErrorExist'),
            })
          } else if (status === 422 && message === 'password does not exist') {
            handlePasswordVerifyModal('email')
          } else if (status === 423 && detail) {
            setModalMFA(false)
            setMFAError('')
            setSuspendTitle(
              detail.suspend_duration
                ? translate('giftShop:attemptLimitExceeded')
                : undefined,
            )
            setCooldownTimer(detail.suspend_duration || detail.wait_time)
            setPasswordLimitModal(true)
          } else if (status === 429 && detail) {
            setModalMFA(false)
            setMFAError('')
            const {hour, minute, second} = formatCooldownTime(
              totp ? detail.request_in : detail.resend_in,
              false,
              true,
            )
            showSnackbar(
              translate('auth:resendLimitReached', {
                duration: hour + minute + second,
              }),
            )
          } else {
            setModalMFA(false)
            setMFAError('')
            setErrorMessage({
              type: '' as ProfileEditEmailFieldType,
              message: '',
            })
            showSnackbar(translate('global:messageError', {message}))
          }
        },
      })
    },
    [
      email,
      handleOnSendFirstOtpSuccess,
      handlePasswordVerifyModal,
      password,
      setMFAError,
      setMFALoading,
      setModalMFA,
      translate,
    ],
  )

  const handle2FAEditEmailResendCurrent = useCallback(
    (method: ProfileEditVerifMethod) => {
      setLoadingInModal(true)
      requestData('member_2fa_edit_email_resend_current', {
        data: {verification_method: method},
        onRequestReceived: () => {
          setLoadingInModal(false)
          setModalVerifMethod(false)
        },
        onRequestSuccess: ({status, data: {message, detail}}) => {
          if (status === 200) {
            handleOnSendFirstOtpSuccess(method)
          } else if (status === 429 && detail) {
            if (message === 'too many request') {
              const {hour, minute, second} = formatCooldownTime(
                detail.resend_in,
                false,
                true,
              )
              showSnackbar(
                translate('auth:resendLimitReached', {
                  duration: hour + minute + second,
                }),
              )
            }
          } else {
            showSnackbar(
              translate('global:messageError', {context: `code-${status}`}),
            )
          }
        },
      })
    },
    [handleOnSendFirstOtpSuccess, translate],
  )

  const handleSubmitForm = useCallback(() => {
    const emailChanged = newEmail ? newEmail !== email : true
    if (emailChanged) {
      // show modal verif method
      setShouldResendOtp(false)
      update('editProfileState', {verifMethodEmail: undefined})
      handle2FAEditEmail()
    } else if (isOldEmailConfirmed) {
      // on second verif
      handleResendEmail()
    } else if (new Date().getTime() / 1000 - emailTime / 1000 > OTP_COOLDOWN) {
      handle2FAEditEmail()
    } else {
      setModalOtp(true)
    }
  }, [
    email,
    emailTime,
    handle2FAEditEmail,
    handleResendEmail,
    isOldEmailConfirmed,
    newEmail,
    setModalOtp,
    update,
  ])

  const handleRenderModalVerifMethod = useMemo(
    () => (
      <GiftShopEditProfileModalVerifMethod
        visible={modalVerifMethod}
        toggleModal={() => setModalVerifMethod(false)}
        method={isEditEmail ? 'email' : 'phone'}
        loading={loadingInModal}
        isAddCredential={!isEditEmail}
        oldCredential={
          oldEmail || `+${phone_code} ${formatPhoneNumber(phone_number!)}`
        }
        onSelect={(method) => {
          setSelectedMethod(method)
          if (method === 'kyc') {
            setPublishedState(true)
            setModalVerifMethod(false)
            handleOnSendFirstOtpSuccess(method)
          } else {
            if (shouldResendOtp) {
              handle2FAEditEmailResendCurrent(method)
              return
            }
            handle2FAEditEmail(method)
          }
        }}
      />
    ),
    [
      modalVerifMethod,
      isEditEmail,
      loadingInModal,
      oldEmail,
      phone_code,
      phone_number,
      handleOnSendFirstOtpSuccess,
      shouldResendOtp,
      handle2FAEditEmail,
      handle2FAEditEmailResendCurrent,
    ],
  )

  const handleRenderModalOtp = useMemo(
    () =>
      modalOtp ? (
        <GiftShopTemplateOTPModal
          stateError={stateOtpError}
          timeRemaining={emailTime}
          verificationMethod={isEditEmail ? 'email' : 'phone'}
          onResend={() => {
            setModalOtp(false)
            setResetWhenPress(false)
            setModalVerifMethod(true)
          }}
          onVerify={handleVerifyOtp}
          onCancel={() => setModalOtp(false)}
          dataToVerify={
            oldEmail || `+${phone_code} ${formatPhoneNumber(phone_number!)}`
          }
          bottomText={translate(
            isEditEmail
              ? 'giftShop:oldEmail'
              : verifMethodEmail
              ? verifMethodEmail === 'phone'
                ? 'global:mobileNumber'
                : 'global:whatsapp'
              : 'global:whatsapp',
          )}
          textAvailable={translate('giftShop:editCredentialOtpResendAvailable')}
          textCooldown="giftShop:editCredentialOtpResendCooldown"
          resetWhenPress={resetWhenPress}
          footerText={
            otpErrorCode === 410 ? translate('giftShop:codeExpired') : undefined
          }
        />
      ) : null,
    [
      emailTime,
      handleVerifyOtp,
      isEditEmail,
      modalOtp,
      oldEmail,
      otpErrorCode,
      phone_code,
      phone_number,
      resetWhenPress,
      setModalOtp,
      stateOtpError,
      translate,
      verifMethodEmail,
    ],
  )

  const handleRenderModalVerifStatus = useMemo(
    () => (
      <GiftShopEditProfileModalVerifStatus
        visible={modalVerifStatus}
        verifStatus={verifStatus}
        method="email"
        loading={loadingInModal}
        onPrimaryAction={() => {
          setModalVerifStatus(false)
          setVerifStatus(undefined)
          if (verifStatus === 'success') {
            history.push('auth_verification_email', {
              email,
              disableCensor: true,
            })
          }
        }}
      />
    ),
    [email, history, loadingInModal, modalVerifStatus, verifStatus],
  )

  const handleRenderErrorMessage = useMemo(
    () =>
      !!errorMessage && (
        <StyledError color="danger_5" fontWeight="medium">
          {errorMessage.message}
        </StyledError>
      ),
    [errorMessage],
  )

  const handleRenderEmail = useMemo(
    () => (
      <>
        <Input
          type="email"
          name="email"
          form={form}
          formRules={VALIDATION_GIFTSHOP_EDITPROFILE_EMAIL}
          label={translate('global:emailNew')}
          placeholder={translate('giftShop:addEmailPlaceholder')}
          onChangeText={() => {
            setErrorMessage({
              type: '' as ProfileEditEmailFieldType,
              message: '',
            })
          }}
        />
        {errorMessage.type === 'email' && handleRenderErrorMessage}
      </>
    ),
    [errorMessage.type, form, handleRenderErrorMessage, translate],
  )

  const handleRenderPassword = useMemo(
    () => (
      <>
        <Input
          type="password"
          name="password"
          form={form}
          formRules={VALIDATION_AUTH_LOGIN_PASSWORD}
          label={translate('global:password')}
          placeholder={translate('auth:loginInputPasswordPlaceholder')}
          containerStyle={{marginTop: convertUnit(20)}}
          rightIcon="visibility"
          onChangeText={() => {
            setErrorMessage({
              type: '' as ProfileEditEmailFieldType,
              message: '',
            })
          }}
        />
        {errorMessage.type === 'password' && handleRenderErrorMessage}
      </>
    ),
    [errorMessage.type, form, handleRenderErrorMessage, translate],
  )

  const handleRenderPasswordVerifyModal = useMemo(
    () => (
      <GiftShopEditProfilePasswordVerifyModal
        visible={passwordVerifyModal.visible}
        item={passwordVerifyModal.item}
        onCancel={() => {
          handlePasswordVerifyModal('none')
          history.goBack()
        }}
        onConfirm={() => setActiveContent('password')}
      />
    ),
    [
      handlePasswordVerifyModal,
      history,
      passwordVerifyModal.item,
      passwordVerifyModal.visible,
      setActiveContent,
    ],
  )

  const isButtonDisabled = useMemo(() => {
    const isValidEmail = errors.email !== undefined || email === ''
    const isValidPassword = errors.password !== undefined || password === ''

    return isValidEmail || isValidPassword
  }, [email, errors.email, errors.password, password])

  const handleRenderButton = useMemo(
    () => (
      <StyledButton
        disabled={isButtonDisabled}
        isLoading={loading}
        label={translate('global:save')}
        onClick={handleSubmit(handleSubmitForm)}
      />
    ),
    [isButtonDisabled, loading, translate, handleSubmit, handleSubmitForm],
  )

  const handleRenderPasswordLimitModal = useMemo(
    () =>
      passwordLimitModal && (
        <GiftShopEditProfileLimitPasswordModal
          cooldownTimer={cooldownTimer}
          onConfirm={() => setPasswordLimitModal((prev) => !prev)}
          title={suspendTitle}
        />
      ),
    [cooldownTimer, passwordLimitModal, suspendTitle],
  )

  const handleRenderModalMFA = useMemo(
    () => (
      <GiftShopTemplateModalMFA
        stateModal={stateModalMFA}
        stateLoading={stateMFALoading}
        stateError={stateMFAError}
        onConfirm={(code) => handle2FAEditEmail(selectedMethod, code)}
      />
    ),
    [
      handle2FAEditEmail,
      selectedMethod,
      stateMFAError,
      stateMFALoading,
      stateModalMFA,
    ],
  )

  const handleRenderScreen = useMemo(() => {
    switch (screen) {
      case 'kyc':
        return (
          stateKycActiveScreen && (
            <GiftShopEditProfileSelfie
              method="email"
              stateScreen={stateKycActiveScreen}
              statePassword={statePassword}
            />
          )
        )
      default:
        return (
          <>
            {handleRenderEmail}
            {handleRenderPassword}
            {handleRenderButton}
          </>
        )
    }
  }, [
    handleRenderButton,
    handleRenderEmail,
    handleRenderPassword,
    screen,
    stateKycActiveScreen,
    statePassword,
  ])

  useEffect(() => setisFilled(prompt), [prompt, setisFilled])

  useEffect(() => {
    screen === 'form' && setValue('email', newEmail)
  }, [newEmail, screen, setValue])

  useDidMount(() => {
    if (!has_password) {
      handlePasswordVerifyModal('email')
    }
  })

  useEffect(() => {
    if (showModalEmailOtp) {
      update('editProfileState', {showModalEmailOtp: false})
    }
  }, [showModalEmailOtp, update])

  return (
    <StyledContainer>
      {handleRenderPasswordLimitModal}
      {handleRenderPasswordVerifyModal}
      {handleRenderModalVerifMethod}
      {handleRenderModalOtp}
      {handleRenderModalVerifStatus}
      {handleRenderModalMFA}
      <StyledScreenContainer isKyc={screen === 'kyc'}>
        {handleRenderScreen}
      </StyledScreenContainer>
    </StyledContainer>
  )
}
